// client/src/contexts/NetWorthContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import useAuthReset from '../hooks/useAuthReset';

const NetWorthContext = createContext(null);

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  'https://soldikeeper-backend-production.up.railway.app/api';

const createAuthApi = () => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('No authentication token found');
  return axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'x-auth-token': token,
    },
  });
};

export const NetWorthProvider = ({ children }) => {
  const { user } = useAuth();
  const userId = user?._id ?? user?.id ?? null;
  const [netWorthData, setNetWorthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchNetWorth = useCallback(async () => {
    if (!localStorage.getItem('token')) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const api = createAuthApi();
      const res = await api.get('/networth');
      setNetWorthData(res.data);
    } catch (err) {
      // Suppress 401s — AuthContext handles them globally
      if (err.response?.status !== 401) {
        console.error('NetWorthContext fetch error:', err);
        setError(err.response?.data?.message || err.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Privacy guard: clear cached net-worth on identity change so
  // User A's account balances are not visible to User B.
  const resetNetWorthState = useCallback(() => {
    setNetWorthData(null);
    setError(null);
  }, []);
  useAuthReset(resetNetWorthState);

  useEffect(() => {
    if (userId) {
      fetchNetWorth();
    } else {
      setNetWorthData(null);
      setError(null);
      setLoading(false);
    }
  }, [userId, fetchNetWorth]);

  const refresh = useCallback(() => fetchNetWorth(), [fetchNetWorth]);

  return (
    <NetWorthContext.Provider value={{ netWorthData, loading, error, refresh }}>
      {children}
    </NetWorthContext.Provider>
  );
};

export const useNetWorth = () => {
  const ctx = useContext(NetWorthContext);
  if (!ctx) throw new Error('useNetWorth must be used inside <NetWorthProvider>');
  return ctx;
};

export default NetWorthContext;
